import {
  Backdrop,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid2,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import FormTitle from "../../../components/common/form/FormTitle";
import CustomTypography from "../../../components/common/form/CustomTypography";
import AppButton from "../../../components/common/buttons/AppButton";
import SelectAllIcon from "../../../../assets/select-all.png";
import SelectAddressPopup from "../../../components/common/pop-up/SelectAddressPopup";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useStores } from "../../../../use-stores";
import {
  OrderProduct,
  OrderServices,
} from "../../../../models/Order/OrderProduct";
import AddProductPopup from "../AddProductsPopup";
import AddServicePopup from "../AddServicesPopUp";
import { observer } from "mobx-react";
import { Address } from "../../../../models/Address/Address";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNotification } from "../../../../utils/NotificationsContext";
import {
  InvoicePayment,
  InvoicePaymentRequest,
} from "../../../../models/Invoice/Invoice";
import InvoicePaymentInfoTable from "../../../components/common/table/InvoicePaymentInfoTable";
import AddPaymentInfoPopup from "./AddPaymentInfoPopup";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../components/common/pop-up/PermissionBlockLabel ";
import InvoiceProducts from "../../../components/common/table/InvoiceProducts";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InvoiceServices from "../../../components/common/table/InvoiceServices";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import HistoryIcon from "@mui/icons-material/History";
import ActivityLogDrawer from "../../../components/common/drawer/ActivityLogDrawer";
import dayjs, { Dayjs } from "dayjs";
import DatePicker2 from "../../../components/common/textFields/DatePicker2";

const CreateInvoice = () => {
  const theme = useTheme();
  const params = useParams();
  const { invoiceStore, addressStore, userStore, orderStore } = useStores();
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [canOpenProductDialog, setCanOpenProductDialog] = useState(false);
  const [canOpenServiceDialog, setCanOpenServiceDialog] = useState(false);
  const [canOpenPaymentInfoDialog, setCanOpenPaymentInfoDialog] =
    useState(false);
  const [canOpenAddressPopup, setCanOpenAddressPopup] =
    useState<boolean>(false);
  const [billingFromAddress, setBillingFromAddress] = useState<Address>();
  const [billingAddress, setBillingAddress] = useState<Address>();
  const [shippingAddress, setShippingAddress] = useState<Address>();
  const [addressType, setAddressType] = useState("");
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get("invoice-id");
  const { showError, showSuccess } = useNotification();
  const [balanceDue, setBalanceDue] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [canCreateInvoice, setCanCreateNewInvoice] = useState(true);
  const [canAddOrDeletePayment, setCanAddOrDeletePayment] = useState(true);
  const [, setAdvancePayment] = useState(0);
  const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false);
  const [isActivityLoading, setIsActivityLoading] = useState(false);
  const [recalculateLoading, setRecalculateLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    setCanAddOrDeletePayment(
      userStore.hasPermission(EmployeeRoles.EDIT_ORDER_PARTIAL_PAYMENT),
    );

    if (userStore.hasPermission(EmployeeRoles.CREATE_INVOICE_AND_PI)) {
      setCanCreateNewInvoice(
        userStore.hasPermission(EmployeeRoles.CREATE_INVOICE_AND_PI),
      );
      getOrderAndInvoiceDetails();
    } else {
      setCanCreateNewInvoice(
        userStore.hasPermission(EmployeeRoles.CREATE_INVOICE_AND_PI),
      );
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOrderAndInvoiceDetails = () => {
    if (invoiceId && params["order-id"]) {
      orderStore
        .getOrderDetails(Number(params["order-id"]))
        .then(() => {
          invoiceStore
            .getInvoiceDetailsById(Number(invoiceId))
            .then(() => {
              calculateAmountReceivedAndBalanceDue();
              setBillingAddress(invoiceStore.invoiceRequest.billing_address);
              setShippingAddress(invoiceStore.invoiceRequest.shipping_address);
              setBillingFromAddress(
                invoiceStore.invoiceRequest.bill_from_address,
              );
            })
            .catch((err) => {
              showError(
                "Error occurred",
                err?.response?.data?.message ||
                  "Failed to fetch invoice details, please try again later",
              );
            })
            .finally(() => setLoading(false));
          if (orderStore.orderRequest.customer_id) {
            getAddressesByCustomerId(
              Number(orderStore.orderRequest.customer_id),
            );
          }
        })
        .catch((err) => {
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Failed to fetch order details, please try again later",
          );
        })
        .finally(() => setLoading(false));
    } else if (params["order-id"]) {
      orderStore
        .getOrderDetails(Number(params["order-id"]))
        .then(() => {
          if (orderStore.orderRequest.customer_id) {
            calculateAmountReceivedAndBalanceDue();
            getAddressesByCustomerId(
              Number(orderStore.orderRequest.customer_id),
            );
          }
        })
        .catch((err) => {
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Failed to fetch order details, please try again later",
          );
        })
        .finally(() => setLoading(false));
    }
  };

  const calculateAmountReceivedAndBalanceDue = () => {
    let totalPaid = 0;
    if (invoiceStore.invoiceRequest.payments) {
      invoiceStore.invoiceRequest?.payments?.forEach((payment) => {
        totalPaid += Number(payment.amount);
      });
      setTotalPaid(totalPaid);
    }
    setBalanceDue(
      Number(
        (
          (invoiceStore.invoiceDetails.amount || 0) -
          ((invoiceStore.invoiceDetails.advance_payment_amount || 0) +
            totalPaid)
        ).toFixed(2),
      ),
    );
    setAdvancePayment(
      Number(
        (invoiceStore.invoiceDetails.advance_payment_amount || 0).toFixed(2),
      ),
    );
  };

  const handleAddProducts = (selectedProducts: OrderProduct[]) => {
    invoiceStore.updateConfirmedProducts(selectedProducts);
    toggleAddProductsPopup();
  };

  const handleAddServices = (selectedServices: OrderServices[]) => {
    invoiceStore.updateConfirmedServices(selectedServices);
    toggleAddServicesPopup();
  };

  const toggleAddProductsPopup = () => {
    if (canOpenProductDialog) {
      setCanOpenProductDialog(false);
    } else {
      setCanOpenProductDialog(true);
    }
  };

  const toggleAddServicesPopup = () => {
    if (canOpenServiceDialog) {
      setCanOpenServiceDialog(false);
    } else {
      setCanOpenServiceDialog(true);
    }
  };

  const handleDeleteProduct = (id: number) => {
    setLoading(true);
    const itemExistsInInvoice = invoiceStore.invoiceDetails.items?.some(
      (item) => item.product_id === id,
    );
    const orderId = Number(params["order-id"]);
    if (itemExistsInInvoice && invoiceId) {
      invoiceStore
        .deleteInvoice(orderId, Number(invoiceId), id)
        .then(() => {
          calculateAmountReceivedAndBalanceDue();
        })
        .catch((err) => {
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Failed to delete product from invoice, please try again later",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      invoiceStore.deleteProductLocally(id);
    }
    setLoading(false);
  };

  const handleDeleteService = (id: number, custom_name: string) => {
    const itemExistsInInvoice = invoiceStore.invoiceDetails.services?.some(
      (item) => item.service_id === id,
    );
    const orderId = Number(params["order-id"]);
    if (itemExistsInInvoice && invoiceId) {
      setLoading(true);
      invoiceStore
        .deleteInvoice(orderId, Number(invoiceId), undefined, id)
        .then(() => {
          calculateAmountReceivedAndBalanceDue();
        })
        .catch((err) => {
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Failed to delete product from invoice, please try again later",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      invoiceStore.deleteServiceLocally(id, custom_name);
    }
  };

  const toggleAddressesPopup = (addressType?: string) => {
    if (addressType) {
      setAddressType(addressType);
    }

    if (canOpenAddressPopup) {
      setCanOpenAddressPopup(false);
    } else {
      setCanOpenAddressPopup(true);
    }
  };

  const getAddressesByCustomerId = (customerId: number) => {
    addressStore
      .getAddress({
        customer_id: customerId,
      })
      .then()
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Failed to fetch addresses for the customer, please try again later",
        );
      })
      .finally(() => setLoading(false));

    addressStore.getOwnAddresses();
  };

  const updateBillingFromAddress = () => {
    setBillingFromAddress(invoiceStore.invoiceRequest.bill_from_address);
  };

  const updateBillingAddress = () => {
    setBillingAddress(invoiceStore.invoiceRequest.billing_address);
  };

  const updateShippingAddress = () => {
    setShippingAddress(invoiceStore.invoiceRequest.shipping_address);
  };

  const handleUpdateProductQuantity = (id: number, quantity: number) => {
    setLoading(true);
    if (
      invoiceStore.invoiceRequest &&
      invoiceStore.invoiceRequest.items &&
      invoiceStore.invoiceRequest.items.length > 0
    ) {
      invoiceStore.updateProductQuantity(id, quantity);
    } else {
      showError(
        "Error Occurred",
        "Please select atlest one product to update it's quantity",
      );
    }
    setLoading(false);
  };

  const handleUpdateServiceQuantity = (
    id: number,
    custom_name: string,
    quantity: number,
  ) => {
    setLoading(true);
    if (
      invoiceStore.invoiceRequest &&
      invoiceStore.invoiceRequest.services &&
      invoiceStore.invoiceRequest.services.length > 0
    ) {
      invoiceStore.updateServiceQuantity(id, custom_name, quantity);
    } else {
      showError(
        "Error Occurred",
        "Please select atlest one service to update it's quantity",
      );
    }
    setLoading(false);
  };

  const handleSelectionOfAddress = (address: Address) => {
    if (addressType === "billingFromAddress") {
      invoiceStore.updateBillingFromAddress(address);
      updateBillingFromAddress();
    } else if (addressType === "billingAddress") {
      invoiceStore.updateBillingAddress(address);
      updateBillingAddress();
    } else if (addressType === "shippingAddress") {
      invoiceStore.updateShippingAddress(address);
      updateShippingAddress();
    } else {
      showError(
        "Error occurred",
        "Invalid addressType or unable to found address type.",
      );
    }
  };

  const createInvoice = () => {
    setBtnLoading(true);
    invoiceStore
      .createOrUpdateInvoice(Number(params["order-id"]))
      .then(() => {
        navigate(
          `/order/invoice/${params["order-id"]!}?invoice-id=${
            invoiceStore.invoiceDetails.id
          }`,
        );
        calculateAmountReceivedAndBalanceDue();
      })
      .catch((err) =>
        showError(
          "Error occurred",
          invoiceId
            ? err?.response?.data?.message ||
                "Failed to update invoice, please try again later"
            : err?.response?.data?.message ||
                "Failed to create invoice, please try again later",
        ),
      )
      .finally(() => setBtnLoading(false));
  };

  const toggleAddPaymentInfoPopup = () => {
    if (canOpenPaymentInfoDialog) {
      setCanOpenPaymentInfoDialog(false);
    } else {
      setCanOpenPaymentInfoDialog(true);
    }
  };

  const submitPaymentDetails = (addPayment: InvoicePaymentRequest) => {
    setLoading(true);
    addPayment.invoice_id = Number(invoiceStore.invoiceDetails.id);
    addPayment.recorded_date = addPayment.recorded_date
      ? new Date(addPayment.recorded_date).toISOString().split("T")[0]
      : undefined;

    if (!balanceDue) {
      showError("Error Occurred", "Balance due amount not found");
      setLoading(false);
      return;
    }

    if (balanceDue) {
      let paymentTotal = 0;
      paymentTotal += Number(addPayment?.amount!);
      if (paymentTotal > balanceDue) {
        showError(
          "Amount Exceeding",
          "Received amount cannot exceed the total invoice amount.",
        );
        setLoading(false);
        return;
      }
    }

    invoiceStore
      .addPaymentInfo(addPayment)
      .then(() => {
        calculateAmountReceivedAndBalanceDue();
        toggleAddPaymentInfoPopup();
      })
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Failed to update payment info, please try again later",
        );
      })
      .finally(() => setLoading(false));
  };

  const deletePaymentInfo = (deletePayment: InvoicePayment) => {
    setLoading(true);
    if (deletePayment.id && deletePayment.invoice_id) {
      invoiceStore
        .deletePayment(deletePayment)
        .then(() => {
          invoiceStore.removePaymentLocally(deletePayment);
          calculateAmountReceivedAndBalanceDue();
        })
        .catch((err) => {
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Failed to delete payment, please try again later",
          );
        })
        .finally(() => setLoading(false));
    } else {
      showError(
        "Error occurred",
        "Payment id OR Invoice id is missing, please check or try again later",
      );
    }
  };

  const syncInvoiceWithZoho = () => {
    setLoading(true);
    if (invoiceStore.invoiceDetails.id) {
      invoiceStore
        .syncWithZoho(invoiceStore.invoiceDetails.id)
        .then(() => {
          getOrderAndInvoiceDetails();
          showSuccess("The invoice was successfully synced with Zoho.");
        })
        .catch((err) => {
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Failed to sync invoice with Zoho. Please try again later.",
          );
        })
        .finally(() => setLoading(false));
    } else {
      showError(
        "Error occurred",
        "Failed to fetch invoice no, Please try again later.",
      );
      setLoading(false);
    }
  };

  const recalculateInvoice = () => {
    if (!userStore.hasPermission(EmployeeRoles.EDIT_INVOICE)) {
      showError(
        "Permission Denied",
        "You do not have permission to recalculate the invoice.",
      );
      return;
    }
    if (!invoiceStore.invoiceDetails.id) {
      showError("Error occurred", "Invoice ID not found.");
      return;
    }
    setRecalculateLoading(true);
    invoiceStore
      .recalculateInvoice(invoiceStore.invoiceDetails.id)
      .then(() => {
        calculateAmountReceivedAndBalanceDue();
        setBillingAddress(invoiceStore.invoiceRequest.billing_address);
        setShippingAddress(invoiceStore.invoiceRequest.shipping_address);
        setBillingFromAddress(invoiceStore.invoiceRequest.bill_from_address);
        showSuccess("Invoice recalculated successfully.");
      })
      .catch((err) =>
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Failed to recalculate invoice, please try again later",
        ),
      )
      .finally(() => setRecalculateLoading(false));
  };

  const downloadInvoice = () => {
    setLoading(true);
    invoiceStore
      .downloadInvoice(invoiceStore.invoiceDetails.id)
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename
            ? response.filename
            : `Invoice_${invoiceStore.invoiceDetails.id}_${params["order-id"]}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showError("Error Occurred", "Failed to download invoice.");
        }
      })
      .catch((err) => {
        showError(
          "Error Occurred",
          err?.message || "Failed to download invoice, please try again later",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateInvoice = () => {
    invoiceStore
      .createOrUpdateInvoice(
        Number(params["order-id"]),
        invoiceStore.invoiceDetails.id,
      )
      .then(() => {
        calculateAmountReceivedAndBalanceDue();
        showSuccess("Invoice details has been updated successfully.");
      })
      .catch((err) =>
        showError(
          "Error occurred",
          invoiceId
            ? err?.response?.data?.message ||
                "Failed to update invoice, please try again later"
            : err?.response?.data?.message ||
                "Failed to create invoice, please try again later",
        ),
      )
      .finally(() => setBtnLoading(false));
  };

  const handleOpenActivityDrawer = () => {
    if (!invoiceStore.invoiceDetails.id) return;

    setIsActivityDrawerOpen(true);
    setIsActivityLoading(true);
    invoiceStore
      .getInvoiceActivityLogs(Number(invoiceStore.invoiceDetails.id))
      .catch((err) => {
        console.log("error in getInvoiceActivityLogs", err);
      })
      .finally(() => setIsActivityLoading(false));
  };

  return (
    <Container maxWidth="xl">
      {loading ? (
        <Backdrop
          sx={(theme) => ({
            color: "#fff",
            zIndex: theme.zIndex.drawer + 1,
          })}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <>
          {userStore.hasPermission(EmployeeRoles.CREATE_INVOICE_AND_PI) ? (
            <>
              <Box sx={{ paddingY: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    color: theme.palette.customColors.greyTextColor,
                    "&:hover": {
                      color: theme.palette.customColors.greyTextColor,
                    },
                    "&:active": {
                      color: theme.palette.customColors.greyTextColor,
                    },
                    mb: 2,
                  }}
                  onClick={() =>
                    navigate(
                      `/orders/add-orders?orderId=${Number(params["order-id"])}`,
                    )
                  }
                >
                  <ArrowBackIcon sx={{ fontSize: "16px" }} />
                  <span style={{ fontSize: "14px", fontWeight: "600" }}>
                    Back
                  </span>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: { xs: "wrap", md: "nowrap" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CustomTypography variant="heading3" fontWeight={700}>
                      {invoiceId
                        ? `Update Invoice #${invoiceId}`
                        : "Create Invoice"}
                    </CustomTypography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      ml: "auto",
                    }}
                  >
                    {invoiceStore.invoiceDetails.id && (
                      <IconButton
                        onClick={() => downloadInvoice?.()}
                        size="small"
                        sx={{
                          color: theme.palette.customColors.greyTextColor,
                        }}
                      >
                        <DownloadForOfflineIcon
                          sx={{
                            fontSize: 24,
                          }}
                        />
                      </IconButton>
                    )}
                    {userStore.hasPermission(EmployeeRoles.EDIT_INVOICE) ? (
                      <Box>
                        <DatePicker2
                          key={`${
                            invoiceStore.invoiceRequest?.date
                              ? dayjs(invoiceStore.invoiceRequest?.date)
                              : null
                          }`}
                          value={
                            invoiceStore.invoiceRequest.date
                              ? dayjs(invoiceStore.invoiceRequest.date)
                              : null
                          }
                          onChange={(date: Dayjs | null) => {
                            if (!date) return;
                            const formattedDate = date?.format(
                              "YYYY-MM-DD HH:mm:ss",
                            );
                            invoiceStore.updateInvoiceDate(formattedDate);
                          }}
                          label=""
                          disabled={
                            Boolean(invoiceStore.invoiceDetails.id) &&
                            !Boolean(invoiceStore?.invoiceRequest?.active)
                          }
                        />
                      </Box>
                    ) : null}

                    {invoiceStore.invoiceDetails.id && (
                      <IconButton
                        onClick={handleOpenActivityDrawer}
                        size="small"
                        sx={{
                          color: theme.palette.customColors.greyTextColor,
                        }}
                      >
                        <HistoryIcon
                          sx={{
                            fontSize: 24,
                          }}
                        />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
              <Paper
                elevation={1}
                sx={{ padding: 7, mb: 5, paddingBottom: 4.5 }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Box>
                    <Grid2
                      container
                      spacing={4}
                      justifyContent={"space-between"}
                    >
                      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: {
                              xs: "flex-start",
                              md: "flex-start",
                              lg: "space-between",
                              xl: "space-between",
                            },
                            flexWrap: "wrap",
                            gap: "0.688rem",
                            marginBottom: "1em",
                          }}
                        >
                          <Grid2
                            size={{ xs: 12, sm: 12, md: 12, lg: 3, xl: 2 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "1rem",
                                justifyContent: {
                                  xs: "flex-start",
                                  md: "flex-start",
                                  lg: "start",
                                  xl: "start",
                                },
                              }}
                            >
                              <FormTitle
                                fontSize="1em"
                                color="rgba(124, 122, 128, 1)"
                                title="PO No."
                              />
                              <FormTitle
                                color="#000000"
                                title={
                                  orderStore.orderRequest.client_po_number ||
                                  "--"
                                }
                                fontSize="1.3em"
                                textAlignment="center"
                              />
                            </Box>
                          </Grid2>

                          <Grid2
                            size={{ xs: 12, sm: 12, md: 12, lg: 3, xl: 5 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "1rem",
                                justifyContent: {
                                  xs: "flex-start",
                                  md: "flex-start",
                                  lg: "center",
                                  xl: "center",
                                },
                              }}
                            >
                              <FormTitle
                                color="rgba(124, 122, 128, 1)"
                                title="E-Invoice Id."
                                fontSize="1em"
                                textAlignment="center"
                              />
                              <FormTitle
                                color="#000000"
                                title={
                                  invoiceStore.invoiceDetails.einvoice_id?.toString() ||
                                  "--"
                                }
                                fontSize="1.3em"
                                textAlignment="center"
                              />
                            </Box>
                          </Grid2>

                          <Grid2
                            size={{ xs: 12, sm: 12, md: 12, lg: 3, xl: 2 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "1rem",
                                justifyContent: {
                                  xs: "flex-start",
                                  md: "flex-start",
                                  lg: "center",
                                  xl: "center",
                                },
                              }}
                            >
                              <FormTitle
                                color="rgba(124, 122, 128, 1)"
                                title="Order Id."
                                fontSize="1em"
                                textAlignment="center"
                              />
                              <FormTitle
                                color="#000000"
                                title={"#" + params["order-id"]!}
                                fontSize="1.3em"
                                textAlignment="center"
                              />
                            </Box>
                          </Grid2>

                          <Grid2
                            size={{ xs: 12, sm: 12, md: 12, lg: 3, xl: 3 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "1rem",
                                justifyContent: {
                                  xs: "flex-start",
                                  md: "flex-start",
                                  lg: "end",
                                  xl: "end",
                                },
                              }}
                            >
                              <FormTitle
                                color="rgba(124, 122, 128, 1)"
                                title="Balance Due"
                                fontSize="1em"
                              />
                              {!invoiceStore.invoiceDetails.id && (
                                <FormTitle
                                  color="#000000"
                                  title={
                                    orderStore?.orderRequest?.total_amount !=
                                    null
                                      ? String(
                                          Math.round(
                                            Number(
                                              orderStore!.orderRequest!
                                                .total_amount -
                                                (orderStore!.orderRequest!
                                                  .amount_received || 0),
                                            ),
                                          ),
                                        )
                                      : "0"
                                  }
                                  fontSize="1.3em"
                                />
                              )}

                              {params["order-id"] &&
                                invoiceStore.invoiceDetails.id && (
                                  <FormTitle
                                    color="#000000"
                                    title={String(balanceDue) || "0"}
                                    fontSize="1.3em"
                                  />
                                )}
                            </Box>
                          </Grid2>
                        </Box>

                        <Box
                          sx={{
                            borderBottom: 1,
                            border: "0.063 solid #e0e0e0",
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </Box>

                  <Box>
                    <Grid2 container spacing={2.8}>
                      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            marginY: "1.5em",
                          }}
                        >
                          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}>
                            <Card
                              sx={{
                                p: 0,
                                boxShadow: "none",
                                display: "flex",
                                flexDirection: "column",
                                height: "300px",
                                minHeight: "300px",
                                maxHeight: "300px",
                                overflow: "hidden",
                              }}
                            >
                              <CardContent
                                sx={{
                                  padding: 0,
                                  margin: 0,
                                  flexGrow: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <CustomTypography
                                    variant="heading4"
                                    fontWeight={700}
                                  >
                                    Billing From Address
                                  </CustomTypography>
                                </Box>
                                {billingFromAddress ? (
                                  <Box
                                    mt={2}
                                    pt={2}
                                    pb={2}
                                    pl={3}
                                    pr={3}
                                    borderRadius={1}
                                    border={"0.063rem solid #8B8B8B"}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      flexGrow: 1,
                                    }}
                                  >
                                    <Box>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={700}
                                      >
                                        {`${
                                          billingFromAddress?.first_name || "--"
                                        } ${
                                          billingFromAddress?.last_name || ""
                                        }`}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        style={{ marginTop: "0.375rem" }}
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {`${
                                          billingFromAddress?.address1 || "--"
                                        } ${
                                          billingFromAddress?.address2
                                            ? `, ${billingFromAddress?.address2}`
                                            : ""
                                        } ${
                                          billingFromAddress?.address3
                                            ? `, ${billingFromAddress?.address3}`
                                            : ""
                                        }`}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        Contact No:{" "}
                                        {billingFromAddress?.contact_no || "--"}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        GSTIN:{" "}
                                        {billingFromAddress?.gstin || "--"}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        State Code:{" "}
                                        {billingFromAddress?.state_code || "--"}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        Email:{" "}
                                        {billingFromAddress?.email || "--"}
                                      </CustomTypography>

                                      <Box
                                        mt={2}
                                        mb={1}
                                        gap={3}
                                        display={"flex"}
                                      >
                                        <AppButton
                                          customStyles={{ width: "5.313rem" }}
                                          variant="grayscale"
                                          label="Select"
                                          onClick={() => {
                                            toggleAddressesPopup(
                                              "billingFromAddress",
                                            );
                                          }}
                                          startIcon={SelectAllIcon}
                                        />
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        color: `${theme.palette.customColors.purple}`,
                                        alignSelf: "flex-start",
                                      }}
                                    >
                                      <CheckCircleIcon />
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box
                                    mt={2}
                                    pt={2}
                                    pb={2}
                                    pl={3}
                                    pr={3}
                                    borderRadius={1}
                                    border={"0.063rem solid #8B8B8B"}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      flexGrow: 1,
                                    }}
                                  >
                                    <Box>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={700}
                                      >
                                        No billing from address selected yet.
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        style={{ marginTop: "0.375rem" }}
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>

                                      <Box
                                        mt={11}
                                        mb={1}
                                        gap={3}
                                        display={"flex"}
                                      >
                                        <AppButton
                                          customStyles={{ width: "5.313rem" }}
                                          variant="grayscale"
                                          label="Select"
                                          onClick={() =>
                                            toggleAddressesPopup(
                                              "billingFromAddress",
                                            )
                                          }
                                          startIcon={SelectAllIcon}
                                        />
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        color: `${theme.palette.customColors.purple}`,
                                      }}
                                    ></Box>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}>
                            <Card
                              sx={{
                                p: 0,
                                boxShadow: "none",
                                display: "flex",
                                flexDirection: "column",
                                height: "300px",
                                minHeight: "300px",
                                maxHeight: "300px",
                                overflow: "hidden",
                              }}
                            >
                              <CardContent
                                sx={{
                                  padding: 0,
                                  margin: 0,
                                  flexGrow: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <CustomTypography
                                    variant="heading4"
                                    fontWeight={700}
                                  >
                                    Billing Address
                                  </CustomTypography>
                                </Box>
                                {billingAddress ? (
                                  <Box
                                    mt={2}
                                    pt={2}
                                    pb={2}
                                    pl={3}
                                    pr={3}
                                    borderRadius={1}
                                    border={"0.063rem solid #8B8B8B"}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      flexGrow: 1,
                                    }}
                                  >
                                    <Box>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={700}
                                      >
                                        {`${
                                          billingAddress?.first_name || "--"
                                        } ${billingAddress?.last_name || ""}`}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        style={{ marginTop: "0.375rem" }}
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {`${billingAddress?.address1 || "--"} ${
                                          billingAddress?.address2
                                            ? `, ${billingAddress?.address2}`
                                            : ""
                                        } ${
                                          billingAddress?.address3
                                            ? `, ${billingAddress?.address3}`
                                            : ""
                                        }`}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        Contact No:{" "}
                                        {billingAddress?.contact_no || "--"}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        GSTIN: {billingAddress?.gstin || "--"}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        State Code:{" "}
                                        {billingAddress?.state_code || "--"}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        Email: {billingAddress?.email}
                                      </CustomTypography>

                                      <Box
                                        mt={2}
                                        mb={1}
                                        gap={3}
                                        display={"flex"}
                                      >
                                        <AppButton
                                          customStyles={{ width: "5.313rem" }}
                                          variant="grayscale"
                                          label="Select"
                                          onClick={() =>
                                            toggleAddressesPopup(
                                              "billingAddress",
                                            )
                                          }
                                          startIcon={SelectAllIcon}
                                        />
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        color: `${theme.palette.customColors.purple}`,
                                        alignSelf: "flex-start",
                                      }}
                                    >
                                      <CheckCircleIcon />
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box
                                    mt={2}
                                    pt={2}
                                    pb={2}
                                    pl={3}
                                    pr={3}
                                    borderRadius={1}
                                    border={"0.063rem solid #8B8B8B"}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      flexGrow: 1,
                                    }}
                                  >
                                    <Box>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={700}
                                      >
                                        No billing address selected yet.
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        style={{ marginTop: "0.375rem" }}
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>

                                      <Box
                                        mt={11}
                                        mb={1}
                                        gap={3}
                                        display={"flex"}
                                      >
                                        <AppButton
                                          customStyles={{ width: "5.313rem" }}
                                          variant="grayscale"
                                          label="Select"
                                          onClick={() =>
                                            toggleAddressesPopup(
                                              "billingAddress",
                                            )
                                          }
                                          startIcon={SelectAllIcon}
                                        />
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        color: `${theme.palette.customColors.purple}`,
                                      }}
                                    ></Box>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid2>

                          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}>
                            <Card
                              sx={{
                                p: 0,
                                boxShadow: "none",
                                display: "flex",
                                flexDirection: "column",
                                height: "300px",
                                minHeight: "300px",
                                maxHeight: "300px",
                                overflow: "hidden",
                              }}
                            >
                              <CardContent
                                sx={{
                                  padding: 0,
                                  margin: 0,
                                  flexGrow: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <CustomTypography
                                    variant="heading4"
                                    fontWeight={700}
                                  >
                                    Shipping Address
                                  </CustomTypography>
                                </Box>
                                {shippingAddress ? (
                                  <Box
                                    mt={2}
                                    pt={2}
                                    pb={2}
                                    pl={3}
                                    pr={3}
                                    borderRadius={1}
                                    border={"0.063rem solid #8B8B8B"}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      flexGrow: 1,
                                    }}
                                  >
                                    <Box>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={700}
                                      >
                                        {`${
                                          shippingAddress?.first_name || "--"
                                        } ${shippingAddress?.last_name || ""}`}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        style={{ marginTop: "0.375rem" }}
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {`${
                                          shippingAddress?.address1 || "--"
                                        } ${
                                          shippingAddress?.address2
                                            ? `, ${shippingAddress?.address2}`
                                            : ""
                                        } ${
                                          shippingAddress?.address3
                                            ? `, ${shippingAddress?.address3}`
                                            : ""
                                        }`}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        Contact No:{" "}
                                        {shippingAddress?.contact_no || "--"}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        GSTIN: {shippingAddress?.gstin || "--"}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        State Code:{" "}
                                        {shippingAddress?.state_code || "--"}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        Email: {shippingAddress?.email}
                                      </CustomTypography>
                                      <Box
                                        mt={2}
                                        mb={1}
                                        gap={3}
                                        display={"flex"}
                                      >
                                        <AppButton
                                          customStyles={{ width: "5.313rem" }}
                                          variant="grayscale"
                                          label="Select"
                                          onClick={() =>
                                            toggleAddressesPopup(
                                              "shippingAddress",
                                            )
                                          }
                                          startIcon={SelectAllIcon}
                                        />
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        color: `${theme.palette.customColors.purple}`,
                                        alignSelf: "flex-start",
                                      }}
                                    >
                                      <CheckCircleIcon />
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box
                                    mt={2}
                                    pt={2}
                                    pb={2}
                                    pl={3}
                                    pr={3}
                                    borderRadius={1}
                                    border={"0.063rem solid #8B8B8B"}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      flexGrow: 1,
                                    }}
                                  >
                                    <Box>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={700}
                                      >
                                        No shipping address selected yet.
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        style={{ marginTop: "0.375rem" }}
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="caption"
                                        color={
                                          theme.palette.customColors
                                            .greyTextColor
                                        }
                                      >
                                        {""}
                                      </CustomTypography>
                                      <Box
                                        mt={11}
                                        mb={1}
                                        gap={3}
                                        display={"flex"}
                                      >
                                        <AppButton
                                          customStyles={{ width: "5.313rem" }}
                                          variant="grayscale"
                                          label="Select"
                                          onClick={() =>
                                            toggleAddressesPopup(
                                              "shippingAddress",
                                            )
                                          }
                                          startIcon={SelectAllIcon}
                                        />
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        color: `${theme.palette.customColors.purple}`,
                                      }}
                                    ></Box>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid2>
                        </Box>

                        <Box
                          sx={{
                            borderBottom: "0.125rem solid #e0e0e0",
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </Box>

                  <Box sx={{ marginTop: 3 }}>
                    <InvoiceProducts
                      padding={"1"}
                      boxShadow={"none"}
                      handleDeleteProduct={(id: number) =>
                        handleDeleteProduct(id)
                      }
                      items={invoiceStore.invoiceRequest.items}
                      toggleAddProductsPopup={() => toggleAddProductsPopup()}
                      canGiveDiscount={false}
                      canUpdateOfferPrice={false}
                      canUpdateSellingPrice={false}
                      canChangeQuantity={
                        !invoiceStore.invoiceDetails.id && canCreateInvoice
                          ? true
                          : false
                      }
                      updateQuantity={(productId, quantity) =>
                        handleUpdateProductQuantity(productId, quantity)
                      }
                      canSeeTaxes={
                        invoiceStore.invoiceDetails.id ? true : false
                      }
                      canSeeAmount={
                        invoiceStore.invoiceDetails.id ? true : false
                      }
                    />
                  </Box>

                  <Box sx={{ marginTop: 6 }}>
                    <InvoiceServices
                      services={invoiceStore.invoiceRequest.services}
                      padding={"0"}
                      boxShadow={"0"}
                      handleDeleteService={(id, custom_name) =>
                        handleDeleteService(id, custom_name)
                      }
                      toggleServicesPopup={() => toggleAddServicesPopup()}
                      canUpdateOfferPrice={false}
                      canUpdateSellingPrice={false}
                      canShowMrp={false}
                      canChangeQuantity={
                        !invoiceStore.invoiceDetails.id && canCreateInvoice
                          ? true
                          : false
                      }
                      updateQuantity={(serviceId, custom_name, quantity) =>
                        handleUpdateServiceQuantity(
                          serviceId,
                          custom_name,
                          quantity,
                        )
                      }
                      canSeeTaxes={
                        invoiceStore.invoiceDetails.id ? true : false
                      }
                      canSeeAmount={
                        invoiceStore.invoiceDetails.id ? true : false
                      }
                    />
                  </Box>

                  {/* {invoiceStore.invoiceDetails.id && (
                    <Box sx={{ marginTop: 6 }}>
                      <InvoicePaymentInfoTable
                        padding={"0"}
                        boxShadow={"0"}
                        toggleAddServicesPopup={toggleAddPaymentInfoPopup}
                        handleDeletePayment={(deletePayment: InvoicePayment) =>
                          deletePaymentInfo(deletePayment)
                        }
                        payments={invoiceStore.invoiceRequest.payments || []}
                        canAddPayment={
                          invoiceStore.invoiceDetails.id &&
                            canAddOrDeletePayment
                            ? true
                            : false
                        }
                        canDeletePayment={
                          invoiceStore.invoiceDetails.id &&
                            canAddOrDeletePayment
                            ? true
                            : false
                        }
                      />
                    </Box>
                  )} */}

                  <Box sx={{ marginTop: 6 }}>
                    <Grid2 container spacing={2.8}>
                      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        {invoiceStore.invoiceDetails.amount != null && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              borderBottom: "0.063rem solid #e0e0e0",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              Subtotal:
                            </Typography>
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              {
                                invoiceStore.invoiceDetails
                                  .amount_after_discount
                              }
                            </Typography>
                          </Box>
                        )}

                        {invoiceStore.invoiceDetails.taxes != null && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              borderBottom: "0.063rem solid #e0e0e0",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              Taxes
                            </Typography>
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              {invoiceStore.invoiceDetails.taxes}
                            </Typography>
                          </Box>
                        )}

                        {invoiceStore.invoiceDetails.amount_after_discount !=
                          null && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              borderBottom: "0.063rem solid #e0e0e0",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              Final Total
                            </Typography>
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              {invoiceStore.invoiceDetails.amount}
                            </Typography>
                          </Box>
                        )}

                        {invoiceStore.invoiceDetails.id != null && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              borderBottom: "0.063rem solid #e0e0e0",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              Balance Received:
                            </Typography>
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              {invoiceStore.invoiceDetails
                                ?.advance_payment_amount || 0}
                            </Typography>
                          </Box>
                        )}

                        {invoiceStore.invoiceDetails.id != null && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              borderBottom: "0.063rem solid #e0e0e0",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              Payment Against Invoice
                            </Typography>
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              {totalPaid}
                            </Typography>
                          </Box>
                        )}

                        {invoiceStore.invoiceDetails.id != null && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              borderBottom: "0.063rem solid #e0e0e0",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              Balance Due
                            </Typography>
                            <Typography
                              sx={{
                                color: "#6b6b6b",
                                marginTop: "0.4em",
                                marginBottom: "0.4em",
                              }}
                            >
                              {balanceDue}
                            </Typography>
                          </Box>
                        )}
                      </Grid2>
                    </Grid2>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 2,
                        marginTop: "2em",
                      }}
                    >
                      {invoiceStore.invoiceDetails.id && (
                        <AppButton
                          label={
                            recalculateLoading ? (
                              <>
                                <CircularProgress size={16} color="inherit" />{" "}
                                Recalculating...
                              </>
                            ) : (
                              "Recalculate"
                            )
                          }
                          onClick={recalculateInvoice}
                          variant="grayscaleBlock"
                          customStyles={{
                            padding: 3,
                            paddingTop: 1,
                            paddingBottom: 1,
                            margin: 0,
                            fontWeight: 500,
                            fontSize: "1rem",
                            marginBottom: "1rem",
                          }}
                          disabled={recalculateLoading}
                        />
                      )}
                      {invoiceStore.invoiceDetails.id &&
                        !invoiceStore?.invoiceDetails?.einvoice_id && (
                          <AppButton
                            label={"Create E-Invoice"}
                            onClick={syncInvoiceWithZoho}
                            variant="primaryBlock"
                            customStyles={{
                              padding: 3,
                              paddingTop: 1,
                              paddingBottom: 1,
                              margin: 0,
                              fontWeight: 500,
                              fontSize: "1rem",
                              marginBottom: "1rem",
                            }}
                            disabled={
                              Boolean(invoiceStore.invoiceDetails.id) &&
                              !Boolean(invoiceStore?.invoiceRequest?.active)
                            }
                          />
                        )}
                      {btnLoading ? (
                        <AppButton
                          label={
                            <>
                              <CircularProgress size={16} color="inherit" />{" "}
                              ...Loading
                            </>
                          }
                          variant="primaryBlock"
                          customStyles={{
                            padding: 3,
                            paddingTop: 1,
                            paddingBottom: 1,
                            margin: 0,
                            fontWeight: 500,
                            fontSize: "1rem",
                            marginBottom: "1rem",
                          }}
                          onClick={function (): void {}}
                        />
                      ) : (
                        <AppButton
                          label={
                            invoiceStore.invoiceDetails.id
                              ? "Update Invoice"
                              : "Create Invoice"
                          }
                          onClick={() =>
                            invoiceStore.invoiceDetails.id
                              ? updateInvoice()
                              : createInvoice()
                          }
                          variant="primaryBlock"
                          customStyles={{
                            padding: 3,
                            paddingTop: 1,
                            paddingBottom: 1,
                            margin: 0,
                            fontWeight: 500,
                            fontSize: "1rem",
                            marginBottom: "1rem",
                          }}
                          disabled={
                            Boolean(invoiceStore.invoiceDetails.id) &&
                            !Boolean(invoiceStore?.invoiceRequest?.active)
                          }
                        />
                      )}

                      {!invoiceStore.invoiceDetails.id && canCreateInvoice && (
                        <AppButton
                          label="Clear All"
                          onClick={() => {
                            invoiceStore.clearInvoiceDetails();
                            setBillingAddress(undefined);
                            setShippingAddress(undefined);
                            setBillingFromAddress(undefined);
                          }}
                          variant="grayscale"
                          customStyles={{
                            padding: 6.5,
                            paddingTop: 1,
                            paddingBottom: 1,
                            margin: 0,
                            fontWeight: 600,
                            fontSize: "1rem",
                            marginBottom: "1rem",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </>
          ) : (
            <PermissionBlockLabel />
          )}
          {canOpenAddressPopup && (
            <SelectAddressPopup
              id={
                addressType === "billingAddress"
                  ? "billingAddress"
                  : addressType === "billingFromAddress"
                    ? "billingFromAddress"
                    : "shippingAddress"
              }
              selectedAddress={
                invoiceStore.invoiceRequest.billing_address ||
                invoiceStore.invoiceRequest.shipping_address ||
                invoiceStore.invoiceRequest.bill_from_address
                  ? addressType === "billingAddress"
                    ? invoiceStore.invoiceRequest.billing_address!
                    : addressType === "shippingAddress"
                      ? invoiceStore.invoiceRequest.shipping_address!
                      : addressType === "billingFromAddress"
                        ? invoiceStore.invoiceRequest.bill_from_address!
                        : null
                  : null
              }
              list={
                addressType === "billingFromAddress"
                  ? addressStore.ownAddresses
                  : addressStore.addresses
              }
              toggleAddressPopup={toggleAddressesPopup}
              open={canOpenAddressPopup}
              canCreateNewAddress={false}
              canDeleteAddress={false}
              label={""}
              onDone={(address) => {
                if (address) {
                  handleSelectionOfAddress(address);
                }
              }}
              addNewAddressbtnOnClick={function (): void {
                throw new Error("Function not implemented.");
              }}
            />
          )}
          {canOpenProductDialog && (
            <AddProductPopup
              list={orderStore.orderRequest.order_products || []}
              onSubmit={(selectedProducts) =>
                handleAddProducts(selectedProducts)
              }
              onClose={() => {
                toggleAddProductsPopup();
              }}
              handleInvoiceQuantity={true}
            />
          )}
          {canOpenServiceDialog && (
            <AddServicePopup
              isLoading={false}
              list={orderStore.orderRequest.order_services || []}
              onClose={toggleAddServicesPopup}
              onSubmit={(selectedServices) =>
                handleAddServices(selectedServices)
              }
              {...(params["order-id"]
                ? { orderId: Number(params["order-id"]) }
                : {})}
              handleInvoiceQuantity={true}
            />
          )}
          {canOpenPaymentInfoDialog && (
            <AddPaymentInfoPopup
              onClose={toggleAddPaymentInfoPopup}
              onSubmit={(paymentInfo: InvoicePaymentRequest) =>
                submitPaymentDetails(paymentInfo)
              }
            />
          )}
          <ActivityLogDrawer
            isOpen={isActivityDrawerOpen}
            onClose={() => setIsActivityDrawerOpen(false)}
            activityLogs={invoiceStore.activityLogs}
            isLoading={isActivityLoading}
            title="Activity History"
            subtitle={
              invoiceStore.invoiceDetails.id
                ? `Invoice #${invoiceStore.invoiceDetails.id}`
                : null
            }
          />
        </>
      )}
    </Container>
  );
};

export default observer(CreateInvoice);
